import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Spin, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useIsMobile } from '../hooks/useIsMobile';
import { RadarKey, fetchBudget, fetchItems, fetchRounds, fetchStats } from './api';
import { KIND_LABEL, KIND_ORDER, RADAR_TABS, dayLabel, roundLabel } from './helpers';
import PitchDetail from './PitchDetail';
import PitchList from './PitchList';
import StatsBar from './StatsBar';

const { Text } = Typography;

const PAGE_SIZE = 30;

const EMPTY_TEXT: Record<RadarKey, string> = {
  community: '이번 회차에는 사회부 관련 인기글이 없습니다',
  life: '이번 회차에는 새 생활 의문·불만이 없습니다',
  trend: '기록 쌓는 중 — 며칠 치 글이 모이면 주제가 나타납니다',
};

export default function PitchHelper() {
  const isMobile = useIsMobile();
  const [radar, setRadar] = useState<RadarKey>('trend');
  const [roundId, setRoundId] = useState<string | undefined>(undefined);
  const [kind, setKind] = useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);

  const { data: rounds } = useQuery({ queryKey: ['radarRounds', radar], queryFn: () => fetchRounds(radar), refetchOnWindowFocus: false });
  const { data: budget } = useQuery({ queryKey: ['radarBudget'], queryFn: fetchBudget, refetchOnWindowFocus: false });
  const { data: stats } = useQuery({ queryKey: ['radarStats'], queryFn: fetchStats, refetchOnWindowFocus: false });
  const { data, isLoading } = useQuery({
    queryKey: ['radarItems', radar, roundId, kind],
    queryFn: () => fetchItems(radar, roundId, kind),
    refetchOnWindowFocus: false,
  });

  const allItems = useMemo(() => data?.items ?? [], [data]);
  const items = useMemo(() => (showAll ? allItems : allItems.slice(0, PAGE_SIZE)), [allItems, showAll]);
  const hidden = allItems.length - items.length;

  // 데스크톱: 목록이 바뀌면 첫 항목을 자동 선택
  useEffect(() => {
    if (isMobile) return;
    if (!items.length) setSelectedId(undefined);
    else if (!items.some((i) => i.id === selectedId)) setSelectedId(items[0].id);
  }, [items, isMobile, selectedId]);

  const switchRadar = (r: RadarKey) => {
    setRadar(r);
    setShowAll(false);
    setRoundId(undefined);
    setKind(undefined);
    setSelectedId(undefined);
  };

  // 회차(날짜) 좌우 이동: rounds는 최신순. roundId가 없으면 최신
  const roundList = rounds?.rounds ?? [];
  const currentIdx = Math.max(0, roundList.findIndex((r) => r.id === (roundId ?? data?.round?.id)));
  const current = roundList[currentIdx];
  const goTo = (idx: number) => {
    setRoundId(roundList[idx].id);
    setSelectedId(undefined);
  };
  const unit = radar === 'trend' ? '날짜' : '회차';
  const arrowStyle: React.CSSProperties = { width: 30, height: 30, padding: 0 };
  const lifeHeader = radar !== 'trend' && (
    <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 10 }}>
      <div style={{ fontWeight: 700, fontSize: isMobile ? 13 : 14 }}>최근 24시간</div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {radar === 'life'
          ? `${allItems.filter((i) => !i.single).length}건은 여러 곳에서 언급 · 단독 글 ${allItems.filter((i) => i.single).length}건`
          : `주제 ${allItems.length}건 (4시간마다 갱신)`}
      </Text>
    </div>
  );
  const roundNav = radar === 'trend' && current && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 10 }}>
      <Button aria-label={`이전 ${unit}`} style={arrowStyle} disabled={currentIdx >= roundList.length - 1} onClick={() => goTo(currentIdx + 1)}>
        ◀
      </Button>
      <div style={{ flex: isMobile ? 1 : undefined, textAlign: isMobile ? 'center' : undefined }}>
        <div style={{ fontWeight: 700, fontSize: isMobile ? 13 : 14 }}>
          {radar === 'trend' && current.day ? dayLabel(current.day) : roundLabel(current.startedAt, new Date(), isMobile)}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {radar === 'trend' ? `주제 ${current.newCount}` : `새 항목 ${current.newCount}`}
        </Text>
      </div>
      <Button aria-label={`다음 ${unit}`} style={arrowStyle} disabled={currentIdx <= 0} onClick={() => goTo(currentIdx - 1)}>
        ▶
      </Button>
      {!isMobile &&
        (currentIdx > 0 ? (
          <Button type="primary" ghost size="small" shape="round" style={{ marginLeft: 'auto' }} onClick={() => goTo(0)}>
            최신으로 ↻
          </Button>
        ) : (
          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
            {`최신 ${unit}`}
          </Text>
        ))}
    </div>
  );

  const kinds = data?.kinds ?? {};
  const chip = (key: string | undefined, label: string) => (
    <Tag.CheckableTag
      key={key ?? 'all'}
      checked={kind === key}
      onChange={() => {
        setKind(key);
        setShowAll(false);
        setSelectedId(undefined);
      }}
      style={{ border: '1px solid #d9d9d9', fontSize: 12, padding: '1px 10px', borderRadius: 12 }}
    >
      {label}
    </Tag.CheckableTag>
  );
  const kindChips = radar === 'community' && (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
      {chip(undefined, '전체')}
      {KIND_ORDER.filter((k) => kinds[k] || kind === k).map((k) => chip(k, `${KIND_LABEL[k]} ${kinds[k] ?? 0}`))}
    </div>
  );

  const header = (
    <div style={{ margin: '4px 0 12px' }}>
      <div role="tablist" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: isMobile ? 6 : 10 }}>
        {RADAR_TABS.map((t) => {
          const on = t.key === radar;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => switchRadar(t.key)}
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                padding: isMobile ? '10px 8px' : '12px 14px',
                borderRadius: 10,
                border: `2px solid ${on ? '#1677ff' : '#e5e7eb'}`,
                background: on ? '#1677ff' : '#fff',
                color: on ? '#fff' : '#262626',
                boxShadow: on ? '0 2px 8px rgba(22,119,255,0.25)' : 'none',
                transition: 'all .15s',
              }}
            >
              <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, whiteSpace: 'nowrap' }}>
                <span aria-hidden style={{ marginRight: 6 }}>{t.icon}</span>
                {t.label}
              </div>
              {!isMobile && (
                <div style={{ fontSize: 12, marginTop: 2, opacity: on ? 0.9 : 0.6 }}>{t.desc}</div>
              )}
            </button>
          );
        })}
      </div>
      {budget?.stage2Blocked && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            이번 달 분석 한도 도달 — 요약만 표시됩니다
          </Text>
        </div>
      )}
    </div>
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  } else if (!items.length) {
    body = <Empty description={EMPTY_TEXT[radar]} style={{ padding: 40 }} />;
  } else if (isMobile) {
    body = selectedId ? (
      <PitchDetail itemId={selectedId} onBack={() => setSelectedId(undefined)} />
    ) : (
      <>
        <PitchList items={items} onSelect={setSelectedId} />
        {hidden > 0 && (
            <Button type="link" block onClick={() => setShowAll(true)} style={{ margin: '6px 0' }}>
              {`더 보기 (${hidden}건)`}
            </Button>
          )}
      </>
    );
  } else {
    body = (
      <div style={{ display: 'grid', gridTemplateColumns: '42% 58%', border: '1px solid #f0f0f0', borderRadius: 8, minHeight: 420 }}>
        <div style={{ borderRight: '1px solid #f0f0f0', maxHeight: 640, overflowY: 'auto' }}>
          <PitchList items={items} selectedId={selectedId} onSelect={setSelectedId} />
          {hidden > 0 && (
            <Button type="link" block onClick={() => setShowAll(true)} style={{ margin: '6px 0' }}>
              {`더 보기 (${hidden}건)`}
            </Button>
          )}
        </div>
        <div style={{ padding: '12px 16px', maxHeight: 640, overflowY: 'auto' }}>
          {selectedId && <PitchDetail key={selectedId} itemId={selectedId} />}
        </div>
      </div>
    );
  }

  const showChrome = !(isMobile && selectedId);
  return (
    <div>
      {showChrome && (
        <>
          {roundNav}
          {lifeHeader}
          {header}
          {kindChips}
        </>
      )}
      {body}
      {showChrome && radar !== 'trend' && <StatsBar stat={stats?.stats?.[radar]} />}
    </div>
  );
}
